const toInteger = (value) => {
  if (value === '' || value == null) return 0;
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeWorkOrder = (value) => String(value ?? '').trim().toUpperCase();

const rowHasProductionData = (row) => {
  if (!row || typeof row !== 'object') return false;

  const numericFields = ['quantity', 'counter', 'bags_good', 'scrap'];
  return numericFields.some((field) => row[field] !== '' && row[field] != null);
};

const rowHasDowntimeData = (row) => {
  if (!row || typeof row !== 'object') return false;

  return ['code', 'minutes', 'quantity', 'scrap', 'notes'].some((field) => {
    const value = row[field];
    return value !== '' && value != null && (typeof value !== 'string' || value.trim() !== '');
  });
};

const rowHasDowntimeEntryData = (row) => {
  if (!row || typeof row !== 'object') return false;

  return ['code', 'minutes', 'quantity', 'scrap'].some((field) => {
    const value = row[field];
    return value !== '' && value != null && (typeof value !== 'string' || value.trim() !== '');
  });
};

export const validateConversionWorkOrders = (rows) => {
  const groups = new Map();
  const missingWorkOrderRows = [];
  const incompleteItemRows = [];

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    if (!rowHasProductionData(row)) {
      if (String(row?.item ?? '').trim()) incompleteItemRows.push(index + 1);
      return;
    }

    const workOrder = normalizeWorkOrder(row.work_order);
    if (!workOrder) {
      missingWorkOrderRows.push(index + 1);
      return;
    }

    const current = groups.get(workOrder) || {
      workOrder,
      quantity: 0,
      counter: 0,
      bagsGood: 0,
      scrap: 0,
      rowNumbers: [],
    };

    current.quantity += toInteger(row.quantity);
    current.counter += toInteger(row.counter);
    current.bagsGood += toInteger(row.bags_good);
    current.scrap += toInteger(row.scrap);
    current.rowNumbers.push(index + 1);
    groups.set(workOrder, current);
  });

  const workOrderIssues = Array.from(groups.values())
    .map((group) => ({
      ...group,
      netCounter: group.counter - group.scrap,
      quantityMismatch: group.quantity !== group.bagsGood,
      counterMismatch: (group.counter - group.scrap) !== group.bagsGood,
    }))
    .filter((group) => group.quantityMismatch || group.counterMismatch);

  return {
    isValid: missingWorkOrderRows.length === 0 && workOrderIssues.length === 0 && incompleteItemRows.length === 0,
    groups: Array.from(groups.values()),
    missingWorkOrderRows,
    workOrderIssues,
    incompleteItemRows,
  };
};

export const formatWorkOrderIssue = (issue) => {
  const format = (value) => Number(value || 0).toLocaleString('en-US');
  const differences = [];

  if (issue.quantityMismatch) {
    differences.push(`Cantidad ${format(issue.quantity)} ≠ Bolsas buenas ${format(issue.bagsGood)}`);
  }
  if (issue.counterMismatch) {
    differences.push(`Contador - Scrap ${format(issue.netCounter)} ≠ Bolsas buenas ${format(issue.bagsGood)}`);
  }

  return `${issue.workOrder}: ${differences.join('; ')}`;
};

export const validateConversionDowntime = (rows, productionRows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const missingWorkOrderRows = [];
  const missingMinutesRows = [];
  const incompleteRows = [];
  let hasAnyInput = false;

  list.forEach((row, index) => {
    const hasDowntimeData = rowHasDowntimeData(row);
    const hasEntryData = rowHasDowntimeEntryData(row);
    const hasWorkOrder = Boolean(normalizeWorkOrder(row?.work_order));
    const hasRowInput = hasDowntimeData || hasWorkOrder;

    if (!hasRowInput) return;
    hasAnyInput = true;

    if (!hasEntryData) {
      incompleteRows.push(index + 1);
      return;
    }

    if (!hasWorkOrder) missingWorkOrderRows.push(index + 1);
    if (String(row?.code ?? '').trim() && toInteger(row?.minutes) <= 0) {
      missingMinutesRows.push(index + 1);
    }
  });

  const totalQuantity = list.reduce((total, row) => total + toInteger(row?.quantity), 0);
  const totalScrap = list.reduce((total, row) => total + toInteger(row?.scrap), 0);
  const productionList = Array.isArray(productionRows) ? productionRows : [];
  const productionBagsGood = productionList.reduce((total, row) => total + toInteger(row?.bags_good), 0);
  const productionScrap = productionList.reduce((total, row) => total + toInteger(row?.scrap), 0);
  const quantityMismatch = totalQuantity !== productionBagsGood;
  const scrapMismatch = totalScrap !== productionScrap;

  return {
    isValid: hasAnyInput &&
      incompleteRows.length === 0 &&
      missingWorkOrderRows.length === 0 &&
      missingMinutesRows.length === 0 &&
      !quantityMismatch &&
      !scrapMismatch,
    isEmpty: !hasAnyInput,
    incompleteRows,
    missingWorkOrderRows,
    missingMinutesRows,
    totalQuantity,
    totalScrap,
    productionBagsGood,
    productionScrap,
    quantityMismatch,
    scrapMismatch,
  };
};

export const formatConversionDowntimeValidation = (validation) => {
  const messages = [];

  if (validation.isEmpty) {
    messages.push('Agrega al menos un renglón en Mano de Obra A/X antes de cerrar.');
  }
  if (validation.incompleteRows.length > 0) {
    messages.push(`Completa o elimina ${validation.incompleteRows.length === 1 ? 'el renglón' : 'los renglones'} ${validation.incompleteRows.join(', ')} de Mano de Obra A/X.`);
  }
  if (validation.missingWorkOrderRows.length > 0) {
    messages.push(`Falta Work Order en Mano de Obra A/X, ${validation.missingWorkOrderRows.length === 1 ? 'renglón' : 'renglones'} ${validation.missingWorkOrderRows.join(', ')}.`);
  }
  if (validation.missingMinutesRows.length > 0) {
    messages.push(`Faltan minutos para el código capturado en ${validation.missingMinutesRows.length === 1 ? 'el renglón' : 'los renglones'} ${validation.missingMinutesRows.join(', ')}.`);
  }
  if (validation.quantityMismatch) {
    messages.push(`Cantidad A/X (${validation.totalQuantity.toLocaleString()}) ≠ Bolsas buenas de Producción (${validation.productionBagsGood.toLocaleString()}).`);
  }
  if (validation.scrapMismatch) {
    messages.push(`Scrap A/X (${validation.totalScrap.toLocaleString()}) ≠ Scrap de Producción (${validation.productionScrap.toLocaleString()}).`);
  }

  return messages;
};
